import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { SEO } from '@/components/SEO';
import { IMAGES } from '@/assets/images';
import { PROFILES, CAPSULES, CONTACT, TIMELINE, CONSTAT_POINTS, FORMATION_TYPES, CTA_STEPS, ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
function ScrollReveal({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 48,
    scale: 0.95
  }} animate={isInView ? {
    opacity: 1,
    y: 0,
    scale: 1
  } : {
    opacity: 0,
    y: 48,
    scale: 0.95
  }} transition={{
    duration: 0.9,
    ease: [0.22, 1, 0.36, 1],
    delay
  }}>
      {children}
    </motion.div>;
}
export default function Home() {
  return <Layout>
      <SEO 
        title="Shut Up & Close — Formation Setting & Closing B2C & B2B"
        description="Formation professionnelle en Setting et Closing pour vendre sans forcer. Méthode terrain pour B2C et B2B. Par Camille Vega."
        canonical="https://camillevega.fr/"
        type="website"
      />
      <section id="hero" className="relative min-h-screen bg-white overflow-hidden">
        <div className="container mx-auto px-8 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center min-h-screen">
            <motion.div initial={{
            opacity: 0,
            x: -60
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1]
          }}>
              <div className="mb-6">
                <span className="inline-block px-4 py-2 rounded-full bg-[#C9793A]/10 text-[#C9793A] text-sm font-medium tracking-wide">
                  Formation · Closing · Reconversion
                </span>
              </div>

              <h1 className="mb-6">
                <span className="block font-['DM_Sans'] text-5xl md:text-7xl lg:text-8xl font-bold text-[#1d1d1f] leading-[1.05] tracking-[-0.02em]">
                  Shut Up & Close.
                </span>
              </h1>

              <p className="mb-12 font-['DM_Sans'] text-xl md:text-2xl font-normal text-[#6e6e73] leading-relaxed">
                Une méthode construite sur le terrain, pas dans les livres. Pour vendre juste, assumer sa valeur et construire ce qui dure.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#formations" onClick={e => {
                e.preventDefault();
                const element = document.getElementById('formations');
                if (element) {
                  element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }} className="inline-flex items-center justify-center px-8 py-4 bg-[#C9793A] text-white text-base font-semibold rounded-full hover:bg-[#E8A050] transition-all duration-300 hover:scale-105 shadow-lg cursor-pointer">
                  Découvrir la formation
                </a>
                <a href="#parcours" onClick={e => {
                e.preventDefault();
                const element = document.getElementById('parcours');
                if (element) {
                  element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }} className="inline-flex items-center justify-center px-8 py-4 text-[#C9793A] text-base font-semibold hover:text-[#E8A050] transition-all duration-300 cursor-pointer">
                  Mon parcours →
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 60
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1],
            delay: 0.2
          }} className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img src={IMAGES.CAMILLE_HERO} alt="Camille Vega de Hoyos" className="w-full h-auto" />
              </div>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
          <motion.div animate={{
          y: [0, 12, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }} className="text-[#6e6e73] text-2xl">
            ↓
          </motion.div>
        </div>
      </section>

      <section id="constat" className="bg-white py-32 md:py-40">
        <div className="container mx-auto px-8 max-w-6xl">
          <ScrollReveal>
            <h2 className="font-['DM_Sans'] text-4xl md:text-6xl font-bold text-[#1d1d1f] leading-[1.1] tracking-tight text-center mb-24">
              Le marché regorge de formations.
              <br />
              Les résultats, eux, se font rares.
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-12 md:gap-16">

            {CONSTAT_POINTS.map((point, index) => <ScrollReveal key={point.id} delay={index * 0.15}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C9793A]/10 flex items-center justify-center">
                    <span className="font-['DM_Sans'] text-2xl font-bold text-[#C9793A]">
                      {point.number}
                    </span>
                  </div>
                  <h3 className="font-['DM_Sans'] text-xl font-semibold text-[#1d1d1f] mb-4">
                    {point.title}
                  </h3>
                  <p className="font-['DM_Sans'] text-base font-normal text-[#6e6e73] leading-relaxed">
                    {point.description}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      <section id="parcours" className="bg-white py-32 md:py-40">
        <div className="container mx-auto px-8 max-w-6xl">
          <ScrollReveal>
            <div className="text-center mb-20">
              <h2 className="font-['DM_Sans'] text-4xl md:text-6xl font-bold text-[#1d1d1f] mb-6 leading-[1.1] tracking-tight">
                Forgée par le terrain,
                <br />
                pas les manuels.
              </h2>
              <p className="font-['DM_Sans'] text-xl md:text-2xl font-normal text-[#6e6e73] leading-relaxed max-w-3xl mx-auto">
                Mon parcours ne rentre dans aucune case — et c'est précisément ce qui fait la solidité de ce que j'enseigne.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <p className="font-['DM_Sans'] text-lg md:text-xl font-normal text-[#6e6e73] leading-relaxed mb-6">
                Je ne suis pas issue du monde entrepreneurial. Ma famille, c'est celle du travail manuel, de l'effort concret, de la valeur qu'on donne avant de la recevoir. C'est là que j'ai appris ce que veut dire s'investir pour de vrai.
              </p>
              <p className="font-['DM_Sans'] text-lg md:text-xl font-normal text-[#6e6e73] leading-relaxed mb-6">
                Mon parcours m'a conduite à travers l'armée française, l'industrie, la maroquinerie, la joaillerie, la restauration — et enfin, la vente. Des univers radicalement différents, mais un fil commun : comprendre les gens, s'adapter, convaincre, livrer.
              </p>
              <p className="font-['DM_Sans'] text-lg md:text-xl font-normal text-[#6e6e73] leading-relaxed mb-6">
                C'est sur le terrain commercial que tout s'est cristallisé. Des centaines de portes frappées, des centaines de "non" encaissés — et une compréhension profonde de ce qui fait qu'une approche fonctionne, peu importe ce qu'on vend.
              </p>
              <p className="font-['DM_Sans'] text-lg md:text-xl font-normal text-[#6e6e73] leading-relaxed">
                J'ai réalisé que je pouvais faire exactement la même chose dans le digital, avec bien plus d'impact — en transmettant mon propre produit plutôt qu'en vendant des systèmes d'alarme.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <ScrollReveal delay={0.1}>
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img src={IMAGES.CAMILLE_PROFILE_1} alt="Camille Vega de Hoyos - Portrait professionnel" className="w-full h-auto" />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img src={IMAGES.CAMILLE_PROFILE_2} alt="Camille Vega de Hoyos - Portrait professionnel" className="w-full h-auto" />
              </div>
            </ScrollReveal>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-20">
            {TIMELINE.map((item, index) => <ScrollReveal key={item.id} delay={index * 0.15}>
                <div className="bg-[#f5f5f7] rounded-3xl p-8 hover:bg-[#e8e8ed] transition-all duration-500">
                  <div className="text-sm font-semibold text-[#C9793A] mb-3 uppercase tracking-wide">
                    {item.period}
                  </div>
                  <h3 className="font-['DM_Sans'] text-2xl font-bold text-[#1d1d1f] mb-4">
                    {item.title}
                  </h3>
                  <p className="font-['DM_Sans'] text-base font-normal text-[#6e6e73] leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>

          <ScrollReveal delay={0.4}>
            <div className="text-center max-w-4xl mx-auto">
              <p className="font-['DM_Sans'] text-3xl md:text-5xl font-semibold text-[#1d1d1f] leading-tight">
                « Je ne t'inspire pas. Je t'active. »
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section id="formation" className="bg-[#f5f5f7] py-32 md:py-40">
        <div className="container mx-auto px-8 max-w-6xl">
          <ScrollReveal>
            <div className="text-center mb-20">
              <h2 className="font-['DM_Sans'] text-4xl md:text-6xl font-bold text-[#1d1d1f] mb-6 leading-[1.1] tracking-tight">
                Shut Up & Close
                <br />
                Deux façons d'apprendre.
              </h2>
              <p className="font-['DM_Sans'] text-xl md:text-2xl font-normal text-[#6e6e73] max-w-3xl mx-auto leading-relaxed">
                Selon ton rythme, ton niveau et tes objectifs.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-6 mb-24">
            {FORMATION_TYPES.map((type, index) => <ScrollReveal key={type.id} delay={index * 0.15}>
                <div className="bg-white rounded-3xl p-10 hover:shadow-2xl transition-all duration-500">
                  <h3 className="font-['DM_Sans'] text-3xl font-bold text-[#1d1d1f] mb-8">
                    {type.title}
                  </h3>
                  <ul className="space-y-4">
                    {type.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-[#C9793A] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="font-['DM_Sans'] text-base font-normal text-[#1d1d1f] leading-relaxed">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </ScrollReveal>)}
          </div>

          <ScrollReveal>
            <div className="text-center mb-16">
              <h3 className="font-['DM_Sans'] text-2xl md:text-3xl font-semibold text-[#1d1d1f] mb-12">
                Les capsules
              </h3>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-6">
            {CAPSULES.map((capsule, index) => <ScrollReveal key={capsule.id} delay={index * 0.15}>
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C9793A] flex items-center justify-center">
                    <span className="font-['DM_Sans'] text-3xl font-bold text-white">
                      {capsule.number}
                    </span>
                  </div>
                  <h3 className="font-['DM_Sans'] text-xl font-semibold text-[#1d1d1f] mb-3">
                    {capsule.title}
                  </h3>
                  <p className="font-['DM_Sans'] text-base font-normal text-[#6e6e73] leading-relaxed">
                    {capsule.description}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      <section id="pour-qui" className="bg-white py-32 md:py-40">
        <div className="container mx-auto px-8 max-w-6xl">
          <ScrollReveal>
            <h2 className="font-['DM_Sans'] text-4xl md:text-6xl font-bold text-[#1d1d1f] mb-20 text-center leading-[1.1] tracking-tight">
              Cette formation est faite pour toi, si —
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-6">
            {PROFILES.map((profile, index) => <ScrollReveal key={profile.id} delay={index * 0.15}>
                <div className="bg-[#f5f5f7] rounded-3xl p-10 hover:bg-[#e8e8ed] transition-all duration-500">
                  <div className="text-sm font-semibold text-[#C9793A] mb-4 uppercase tracking-wide">
                    {profile.number}
                  </div>
                  <h3 className="font-['DM_Sans'] text-2xl font-bold text-[#1d1d1f] mb-4">
                    {profile.title}
                  </h3>
                  <p className="font-['DM_Sans'] text-base font-normal text-[#6e6e73] leading-relaxed">
                    {profile.description}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      <section id="formations" className="bg-white py-32 md:py-40">
        <div className="container mx-auto px-8 max-w-6xl">
          <ScrollReveal>
            <div className="text-center mb-20">
              <h2 className="font-['DM_Sans'] text-4xl md:text-6xl font-bold text-[#1d1d1f] mb-6 leading-[1.1] tracking-tight">
                Une formation.
                <br />
                Deux profils.
              </h2>
              <p className="font-['DM_Sans'] text-xl md:text-2xl font-normal text-[#6e6e73] leading-relaxed max-w-3xl mx-auto">
                Une seule formation, conçue aussi bien pour les professionnels que pour les particuliers. Le contenu est le même — c'est l'application qui diffère selon ton univers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.1}>
              <div className="bg-[#2C1810] rounded-3xl p-10 hover:shadow-2xl transition-all duration-500 group flex flex-col h-full">
                <div className="mb-6">
                  <span className="inline-block px-4 py-2 rounded-full bg-[#E8651A]/20 text-[#E8651A] text-sm font-medium tracking-wide">
                    Formation B2C
                  </span>
                </div>
                <h3 className="font-['DM_Sans'] text-3xl md:text-4xl font-bold text-[#F5F0E8] mb-6 leading-tight">Tu veux vendre sans forcer. On t'apprend comment.</h3>
                <p className="font-['DM_Sans'] text-base font-normal text-[#B8A898] leading-relaxed mb-8">
                  La vraie vente, ce n'est pas convaincre. C'est créer les conditions où ton prospect décide de lui-même. Setting, closing, posture — maîtrise complète en 6 mois.
                </p>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-[#F5F0E8]">
                    <svg className="w-5 h-5 text-[#E8651A]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-['DM_Sans'] text-sm">6 mois pour monétiser</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#F5F0E8]">
                    <svg className="w-5 h-5 text-[#E8651A]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-['DM_Sans'] text-sm">20% commission dès l'apprentissage</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#F5F0E8]">
                    <svg className="w-5 h-5 text-[#E8651A]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-['DM_Sans'] text-sm">0 roleplay. Du réel uniquement.</span>
                  </div>
                </div>
                <Link to={ROUTE_PATHS.FORMATION_B2C} className="inline-flex items-center justify-center w-full px-8 py-4 bg-[#E8651A] text-[#2C1810] text-base font-semibold rounded-full hover:bg-[#C4521A] transition-all duration-300 hover:scale-105 shadow-lg mt-auto">
                  Découvrir la formation B2C
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="bg-[#1E130C] rounded-3xl p-10 hover:shadow-2xl transition-all duration-500 group flex flex-col h-full">
                <div className="mb-6">
                  <span className="inline-block px-4 py-2 rounded-full bg-[#D4580E]/20 text-[#D4580E] text-sm font-medium tracking-wide">
                    Formation B2B
                  </span>
                </div>
                <h3 className="font-['DM_Sans'] text-3xl md:text-4xl font-bold text-[#F5F0E8] mb-6 leading-tight">Se VENDRE sans  jamais forcer la décision.</h3>
                <p className="font-['DM_Sans'] text-base font-normal text-[#9A8878] leading-relaxed mb-8">
                  Le commercial B2B de 2026 n'est plus un vendeur. C'est un diagnosticien stratégique qui révèle les blocages, qualifie chirurgicalement et guide naturellement vers une décision éclairée.
                </p>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-[#F5F0E8]">
                    <svg className="w-5 h-5 text-[#D4580E]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-['DM_Sans'] text-sm">Définition clair de la cible et de l’avatar type</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#F5F0E8]">
                    <svg className="w-5 h-5 text-[#D4580E]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-['DM_Sans'] text-sm">Analysé des profils décisionnels</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#F5F0E8]">
                    <svg className="w-5 h-5 text-[#D4580E]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-['DM_Sans'] text-sm">0 roleplay. Terrain réel uniquement.</span>
                  </div>
                </div>
                <Link to={ROUTE_PATHS.FORMATION_B2B} className="inline-flex items-center justify-center w-full px-8 py-4 bg-[#D4580E] text-[#1E130C] text-base font-semibold rounded-full hover:bg-[#E8651A] transition-all duration-300 hover:scale-105 shadow-lg mt-auto">
                  Découvrir la formation B2B
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section id="cta" className="bg-[#f5f5f7] py-32 md:py-40">
        <div className="container mx-auto px-8 max-w-4xl text-center">
          <ScrollReveal>
            <h2 className="font-['DM_Sans'] text-4xl md:text-6xl font-bold text-[#1d1d1f] mb-8 leading-[1.1] tracking-tight">
              Un appel. 30 minutes. Un cap.
            </h2>
            <p className="font-['DM_Sans'] text-xl md:text-2xl font-normal text-[#6e6e73] mb-16 leading-relaxed max-w-2xl mx-auto">
              Réserve un créneau de 30 minutes. On fait le point ensemble. Tu repars avec de la clarté.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <a href={CONTACT.ctaForm} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-12 py-5 bg-[#C9793A] text-white text-lg font-semibold rounded-full hover:bg-[#E8A050] transition-all duration-300 hover:scale-105 shadow-lg">
              Réserver mon appel gratuit
            </a>
            <p className="font-['DM_Sans'] text-sm font-normal text-[#6e6e73] mt-6">
              Appel offert · Sans engagement · 30 minutes
            </p>
          </ScrollReveal>
        </div>
      </section>
    </Layout>;
}